import clsx from 'clsx';
import { useStore } from 'effector-react';
import { ChangeEventHandler, FC, memo, useCallback, useEffect, useRef } from 'react';

import styles from './items-without-filters-with-id.module.scss';

import { IItemsWithoutFiltersWithIdProps } from './types';

import { Item, QueryNotFound } from '..';
import { Breadcrumbs, Loader, Pagination, SortSelect, Title } from '../ui';

import {
  $catalogCommonStore,
  $currentDataWithoutFiltersStore,
  getCatalogCommonFx,
  removeCommonItems,
  removeCurrentDataWithoutFilters,
  saveCurrentDataWithoutFilters,
  saveCurrentPaginationPageWithoutFilters,
  saveCurrentSortWithoutFilters,
  updateCatalogCommonBtnFx,
  updateCommonDataImperative,
} from 'src/models';
import { TSortingItems } from '~utils';

export const ItemsWithoutFiltersWithId: FC<IItemsWithoutFiltersWithIdProps> = memo(
  ({ title, fetchFn, additionalQuery = '', className = '', ...rest }) => {
    const { data: currData, status } = useStore($catalogCommonStore);
    const prevData = useStore($currentDataWithoutFiltersStore);

    const key = globalThis.location.pathname.split('/').at(-1)!;

    const data = currData ?? prevData[key]?.data;
    const currSort = prevData[key]?.sort ?? '-is_hit';

    const sortRef = useRef<TSortingItems>(currSort);

    const handleSortingChange: ChangeEventHandler<HTMLSelectElement> = useCallback(
      e => {
        saveCurrentSortWithoutFilters({ path: key, sort: e.target.value as TSortingItems });
        saveCurrentPaginationPageWithoutFilters({ path: key, page: 1 });
      },
      [key],
    );

    const handleSetPage = (path: string) => (page: number) => {
      saveCurrentPaginationPageWithoutFilters({ path, page });
    };

    useEffect(() => {
      const preventData = prevData[key]?.data;
      if (preventData && sortRef.current === currSort) {
        updateCommonDataImperative(preventData);
        removeCurrentDataWithoutFilters(key);
        return;
      }

      // нужно, чтобы после возвращения из карточки сортировка работала
      sortRef.current = currSort;

      removeCurrentDataWithoutFilters(key);

      getCatalogCommonFx({
        productId: key,
        queries: [`sort=${currSort}`, additionalQuery],
        fnWithId: fetchFn,
      });
    }, [currSort, additionalQuery, fetchFn, key]);

    useEffect(() => () => removeCommonItems(), []);

    if (!data) return <Loader />;

    return (
      <div className={clsx(styles.container, className)} {...rest}>
        <Breadcrumbs isSimilar />
        <div className={styles.title_box}>
          <Title className={styles.title}>{title}</Title>
        </div>

        {!!data?.data.length && (
          <SortSelect value={currSort} onChange={handleSortingChange} className={styles.sort_box} />
        )}
        {data.data.length ? (
          <ul className={styles.list}>
            {data.data.map(item => (
              <Item
                key={item.id}
                data={item}
                isCartButton
                handleClick={() => {
                  saveCurrentDataWithoutFilters({
                    data,
                    path: key,
                  });
                }}
              />
            ))}
          </ul>
        ) : (
          <QueryNotFound className={styles.not_found} />
        )}
        {data.meta.pagination.num_pages !== 1 && (
          <Pagination
            className={styles.pagination}
            amountPages={data.meta.pagination.num_pages}
            currSort={currSort}
            onClick={getCatalogCommonFx}
            onBtnLoadClick={updateCatalogCommonBtnFx}
            additionalQuery={additionalQuery}
            productId={key}
            callbackWithId={fetchFn}
            setPage={handleSetPage(key)}
          />
        )}
        {status === 'LOADING' && <Loader withOverlay />}
      </div>
    );
  },
);
